import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
    FileText,
    FileSpreadsheet,
    FileImage,
    File,
    ChevronRight,
    Rocket,
    ClipboardList,
    Search,
    FolderOpen,
    Sparkles,
    Library,
    ArrowLeft,
    Lock,
    MoreVertical,
    Download,
    Eye,
    CheckCircle,
    X,
    Users,
    User,
    Briefcase,
    Folder,
    RefreshCw,
    Trash2,
    Bot
} from 'lucide-react';
import api from '../utils/api';
import FileAcknowledgmentModal from '../components/Files/FileAcknowledgmentModal';
import EmployeeFilePreviewModal from '../components/Files/EmployeeFilePreviewModal';
import EmployeeDocumentsAIPanel from '../components/Files/EmployeeDocumentsAIPanel';
import CollectionCard from '../components/Files/CollectionCard';

const EmployeeFilesPage = () => {
    const [files, setFiles] = useState([]);
    const [collections, setCollections] = useState([]);
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [collectionFiles, setCollectionFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAIPanel, setShowAIPanel] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileToAcknowledge, setFileToAcknowledge] = useState(null);
    const [activeTab, setActiveTab] = useState('all'); // 'all', 'collections'
    const [searchQuery, setSearchQuery] = useState('');
    const [lifecycleFilter, setLifecycleFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [openMenuId, setOpenMenuId] = useState(null);

    // Lifecycle filter options
    const lifecycleOptions = [
        { id: 'all', label: 'All' },
        { id: 'preboarding', label: 'Preboarding' },
        { id: 'onboarding', label: 'Onboarding' },
    ];

    // Category filter options
    const categoryOptions = [
        { id: 'all', label: 'All Documents' },
        { id: 'company-wide', label: 'Company-Wide' },
        { id: 'role-specific', label: 'Role-Specific' },
        { id: 'employee-specific', label: 'For You' },
    ];

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    useEffect(() => {
        fetchFiles(1);
        fetchCollections();
    }, []);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchFiles(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Reset page on filter change
    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchFiles(1);
    }, [lifecycleFilter, categoryFilter]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openMenuId && !event.target.closest('.dropdown-menu')) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openMenuId]);

    const fetchCollections = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/collections/employee/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCollections(response.data.collections);
            }
        } catch (error) {
            console.error('Error fetching collections:', error);
        }
    };

    const handleCollectionClick = async (collection) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/collections/${collection.id}/employee`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setSelectedCollection(response.data.collection);
                setCollectionFiles(response.data.files);
            }
        } catch (error) {
            console.error('Error fetching collection files:', error);
            toast.error('Failed to load collection');
        }
    };

    const fetchFiles = async (pageNum = pagination.page) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params = {
                page: pageNum,
                limit: pagination.limit
            };

            if (searchQuery.trim()) {
                params.search = searchQuery;
            }

            if (lifecycleFilter !== 'all') {
                params.phase = lifecycleFilter;
            }

            if (categoryFilter !== 'all') {
                params.file_category = categoryFilter;
            }

            const response = await api.get('/files/employee', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            if (response.data.success) {
                setFiles(response.data.files);
                if (response.data.pagination) {
                    setPagination({
                        page: response.data.pagination.page,
                        limit: response.data.pagination.limit,
                        total: response.data.pagination.total,
                        pages: response.data.pagination.pages
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching files:', error);
            toast.error('Failed to fetch documents');
        } finally {
            setLoading(false);
        }
    };

    const displayFiles = files;

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.pages) {
            setPagination(prev => ({ ...prev, page: newPage }));
            fetchFiles(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleViewFile = (file) => {
        setSelectedFile(file);
        setShowPreviewModal(true);
        setOpenMenuId(null);
    };

    const handleDownloadFile = async (file) => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/files/employee/${file.id}/signed-url`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.success) {
                const link = document.createElement('a');
                link.href = response.data.signedUrl;
                link.download = file.file_name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success('Download started');
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error('Failed to download document');
        }
        setOpenMenuId(null);
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'Unknown';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleAcknowledge = (file) => {
        setFileToAcknowledge(file);
        setOpenMenuId(null);
    };

    const handleAcknowledgeSuccess = () => {
        fetchFiles();
        toast.success('Acknowledgment recorded successfully');
    };

    const handleDeleteFile = async (file) => {
        if (!window.confirm(`Are you sure you want to delete "${file.file_name}"? This cannot be undone.`)) {
            return;
        }
        setOpenMenuId(null);
        try {
            const response = await api.delete(`/files/employee/${file.id}`);
            if (response.data.success) {
                toast.success('File deleted successfully');
                fetchFiles();
            }
        } catch (error) {
            console.error('Error deleting file:', error);
            toast.error(error.response?.data?.message || 'Failed to delete file');
        }
    };

    // Get file icon based on type
    const getFileIcon = (fileType) => {
        if (!fileType) return File;
        if (fileType.includes('pdf')) return FileText;
        if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType.includes('csv')) return FileSpreadsheet;
        if (fileType.includes('image')) return FileImage;
        if (fileType.includes('word') || fileType.includes('document')) return FileText;
        return File;
    };

    // Get lifecycle badge styling
    const getLifecycleBadge = (phase) => {
        switch (phase) {
            case 'preboarding':
                return {
                    bg: 'bg-amber-100',
                    text: 'text-amber-700',
                    label: 'Preboarding'
                };
            case 'onboarding':
                return {
                    bg: 'bg-blue-50',
                    text: 'text-blue-700',
                    label: 'Onboarding'
                };
            case 'both':
                return {
                    bg: 'bg-purple-100',
                    text: 'text-purple-700',
                    label: 'Both'
                };
            case 'private':
                return {
                    bg: 'bg-gray-100',
                    text: 'text-gray-600',
                    label: 'Personal'
                };
            default:
                return {
                    bg: 'bg-muted',
                    text: 'text-muted-foreground',
                    label: phase || 'Unknown'
                };
        }
    };

    // Get category badge styling
    const getCategoryBadge = (file) => {
        const category = file.file_category;
        switch (category) {
            case 'company-wide':
                return {
                    icon: Users,
                    bg: 'bg-muted',
                    text: 'text-muted-foreground',
                    label: 'Everyone'
                };
            case 'role-specific':
                return {
                    icon: Briefcase,
                    bg: 'bg-muted',
                    text: 'text-muted-foreground',
                    label: `Role: ${file.role_specific || 'N/A'}`
                };
            case 'employee-specific':
                return {
                    icon: User,
                    bg: 'bg-emerald-100',
                    text: 'text-emerald-700',
                    label: 'For You'
                };
            case 'private':
                return {
                    icon: Bot,
                    bg: 'bg-purple-100',
                    text: 'text-purple-700',
                    label: 'AI Generated'
                };
            default:
                return {
                    icon: Users,
                    bg: 'bg-muted',
                    text: 'text-muted-foreground',
                    label: category || 'Unknown'
                };
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex items-start gap-6">
                <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-foreground">My Documents</h1>
                            <p className="text-muted-foreground mt-1">
                                View and download documents shared with you
                            </p>
                        </div>

                        {/* Ask AI Button - Desktop */}
                        <button
                            onClick={() => setShowAIPanel(!showAIPanel)}
                            className={`hidden xl:flex px-4 py-2 rounded-xl font-medium items-center gap-2 transition-all ${showAIPanel ? 'bg-primary/10 text-primary shadow-sm' : 'bg-card text-foreground hover:bg-muted border border-border'}`}
                        >
                            <Sparkles size={18} className={showAIPanel ? 'text-primary' : 'text-muted-foreground'} />
                            {showAIPanel ? 'Close AI Assistant' : 'Ask AI'}
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="bg-card rounded-2xl shadow-sm border border-border mb-6 overflow-hidden">
                        <div className="border-b border-border overflow-x-auto hide-scrollbar">
                            <nav className="flex min-w-max">
                                <button
                                    onClick={() => setActiveTab('all')}
                                    className={`px-6 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-all border-b-2 whitespace-nowrap ${activeTab === 'all'
                                        ? 'border-primary text-primary bg-primary/5'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                                        }`}
                                >
                                    <FolderOpen size={18} />
                                    All Documents
                                    <span className="bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
                                        {files.length}
                                    </span>
                                </button>

                                <button
                                    onClick={() => { setActiveTab('collections'); setSelectedCollection(null); }}
                                    className={`px-6 py-4 text-sm font-medium flex items-center justify-center gap-2 transition-all border-b-2 whitespace-nowrap ${activeTab === 'collections'
                                        ? 'border-primary text-primary bg-primary/5'
                                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                                        }`}
                                >
                                    <Library size={18} />
                                    Collections
                                    {collections.length > 0 && (
                                        <span className="bg-muted text-muted-foreground px-2 py-0.5 rounded-full text-xs">
                                            {collections.length}
                                        </span>
                                    )}
                                </button>
                            </nav>
                        </div>
                    </div>

                    {/* Main Content */}
                    {activeTab === 'collections' ? (
                        <div className="space-y-6">
                            {selectedCollection ? (
                                /* Collection Files View */
                                <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                                    <div className="flex items-center gap-4 mb-6">
                                        <button
                                            onClick={() => setSelectedCollection(null)}
                                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                                        >
                                            <ArrowLeft size={20} className="text-muted-foreground" />
                                        </button>
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: selectedCollection.color + '20' }}
                                        >
                                            <Library size={24} style={{ color: selectedCollection.color }} />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-foreground">{selectedCollection.name}</h2>
                                            {selectedCollection.description && (
                                                <p className="text-sm text-muted-foreground">{selectedCollection.description}</p>
                                            )}
                                        </div>
                                    </div>

                                    {collectionFiles.length > 0 ? (
                                        <div className="divide-y divide-border">
                                            {collectionFiles.map((file) => {
                                                const FileIcon = getFileIcon(file.file_type);
                                                return (
                                                    <div
                                                        key={file.id}
                                                        className="flex items-center justify-between py-4 hover:bg-muted px-2 rounded-lg transition-colors"
                                                    >
                                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                                            <div className="flex-shrink-0 w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                                                                <FileIcon size={20} className="text-red-500" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <button
                                                                    onClick={() => handleViewFile(file)}
                                                                    className="font-medium text-foreground truncate block text-left hover:text-primary transition-colors"
                                                                >
                                                                    {file.file_name}
                                                                </button>
                                                                <p className="text-xs text-muted-foreground">{formatFileSize(file.file_size)}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleViewFile(file)}
                                                                className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                                                title="Preview"
                                                            >
                                                                <Eye size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDownloadFile(file)}
                                                                className="p-2 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
                                                                title="Download"
                                                            >
                                                                <Download size={18} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 text-muted-foreground">
                                            No documents in this collection
                                        </div>
                                    )}
                                </div>
                            ) : (
                                /* Collections Grid */
                                <div className="bg-card rounded-2xl shadow-sm border border-border p-6">
                                    <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                                        <Library size={20} className="text-primary" />
                                        Browse Collections
                                    </h2>
                                    {collections.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                            {collections.map((collection) => (
                                                <CollectionCard
                                                    key={collection.id}
                                                    collection={collection}
                                                    onClick={handleCollectionClick}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <Library size={48} className="mx-auto text-muted-foreground/50 mb-4" />
                                            <h3 className="text-muted-foreground font-medium">No collections available</h3>
                                            <p className="text-muted-foreground/80 text-sm">Check back later for curated file collections</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Filter Bar */}
                            <div className="bg-card rounded-2xl shadow-sm border border-border p-4 mb-6">
                                {/* Row 1: Search + Lifecycle filters */}
                                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                                    {/* Search */}
                                    <div className="relative flex-1 max-w-md w-full">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Search size={18} className="text-muted-foreground" />
                                        </div>
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search documents..."
                                            className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none text-sm placeholder:text-muted-foreground text-foreground"
                                        />
                                        {searchQuery && (
                                            <button
                                                onClick={() => setSearchQuery('')}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            >
                                                <X size={16} className="text-muted-foreground hover:text-foreground" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Lifecycle Filters */}
                                    <div className="flex items-center gap-2">
                                        {lifecycleOptions.map((option) => (
                                            <button
                                                key={option.id}
                                                onClick={() => setLifecycleFilter(option.id)}
                                                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${lifecycleFilter === option.id
                                                    ? option.id === 'preboarding'
                                                        ? 'bg-amber-100 text-amber-700 shadow-sm'
                                                        : option.id === 'onboarding'
                                                            ? 'bg-blue-100 text-blue-700 shadow-sm'
                                                            : 'bg-primary text-white shadow-md shadow-primary/25'
                                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                                    }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Row 2: Category filters + Refresh */}
                                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mt-4 pt-4 border-t border-border">
                                    {/* Category Filters */}
                                    <div className="flex items-center gap-2 overflow-x-auto w-full lg:w-auto hide-scrollbar pb-2 lg:pb-0">
                                        {categoryOptions.map((option) => (
                                            <button
                                                key={option.id}
                                                onClick={() => setCategoryFilter(option.id)}
                                                className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${categoryFilter === option.id
                                                    ? 'bg-primary text-white shadow-md shadow-primary/25'
                                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                                    }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Refresh */}
                                    <button
                                        onClick={() => fetchFiles()}
                                        className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-lg transition-colors flex-shrink-0 hidden lg:block"
                                        title="Refresh"
                                    >
                                        <RefreshCw size={18} />
                                    </button>
                                </div>
                            </div>

                            {/* Document List */}
                            {loading ? (
                                <div className="flex items-center justify-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                                </div>
                            ) : displayFiles.length === 0 ? (
                                <div className="bg-card rounded-2xl shadow-sm border border-border p-12 text-center">
                                    <div className="w-20 h-20 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                                        <FileText size={40} className="text-muted-foreground/50" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-foreground mb-2">No documents found</h3>
                                    <p className="text-muted-foreground">
                                        {searchQuery || lifecycleFilter !== 'all' || categoryFilter !== 'all'
                                            ? 'No documents match your current filters.'
                                            : 'There are no documents shared with you yet.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-card rounded-2xl shadow-sm border border-border">
                                    {/* Table Header */}
                                    <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-muted/50 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider rounded-t-2xl">
                                        <div className="col-span-5">Document</div>
                                        <div className="col-span-2">Lifecycle</div>
                                        <div className="col-span-2">Category</div>
                                        <div className="col-span-2">Added</div>
                                        <div className="col-span-1 text-right">Actions</div>
                                    </div>

                                    {/* Table Body */}
                                    <div className="divide-y divide-border">
                                        {displayFiles.map((file) => {
                                            const FileIconComponent = getFileIcon(file.file_type);
                                            const lifecycle = getLifecycleBadge(file.phase);
                                            const category = getCategoryBadge(file);
                                            const CategoryIcon = category.icon;
                                            const needsAcknowledgment = file.requires_acknowledgment && !file.acknowledged_at;

                                            return (
                                                <div
                                                    key={file.id}
                                                    className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-muted/50 transition-colors"
                                                >
                                                    {/* Document Name */}
                                                    <div className="col-span-5 flex items-center gap-3 min-w-0">
                                                        <div className="flex-shrink-0 w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                                                            <FileIconComponent size={20} className="text-red-500" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => handleViewFile(file)}
                                                                    className="font-medium text-foreground truncate block text-left hover:text-primary transition-colors"
                                                                    title={file.file_name}
                                                                >
                                                                    {file.file_name}
                                                                </button>
                                                                {needsAcknowledgment && (
                                                                    <span className="flex-shrink-0 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                                                        Review Required
                                                                    </span>
                                                                )}
                                                                {file.acknowledged_at && (
                                                                    <CheckCircle size={16} className="flex-shrink-0 text-green-500" title="Acknowledged" />
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-muted-foreground">
                                                                {formatFileSize(file.file_size)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Lifecycle Badge */}
                                                    <div className="col-span-2">
                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${lifecycle.bg} ${lifecycle.text}`}>
                                                            {lifecycle.label}
                                                        </span>
                                                    </div>

                                                    {/* Category Badge */}
                                                    <div className="col-span-2">
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${category.bg} ${category.text}`}>
                                                            <CategoryIcon size={12} />
                                                            {category.label}
                                                        </span>
                                                    </div>

                                                    {/* Added Date */}
                                                    <div className="col-span-2">
                                                        <span className="text-sm text-muted-foreground">
                                                            {formatDate(file.created_at)}
                                                        </span>
                                                    </div>

                                                    {/* Actions Menu */}
                                                    <div className="col-span-1 text-right relative dropdown-menu">
                                                        <button
                                                            onClick={() => setOpenMenuId(openMenuId === file.id ? null : file.id)}
                                                            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                                                        >
                                                            <MoreVertical size={18} />
                                                        </button>

                                                        {openMenuId === file.id && (
                                                            <div className="absolute right-0 top-full mt-1 w-48 bg-card rounded-xl shadow-lg border border-border py-1 z-50">
                                                                <button
                                                                    onClick={() => handleViewFile(file)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                                                                >
                                                                    <Eye size={16} />
                                                                    Preview
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDownloadFile(file)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                                                                >
                                                                    <Download size={16} />
                                                                    Download
                                                                </button>
                                                                {needsAcknowledgment && (
                                                                    <>
                                                                        <div className="border-t border-border my-1"></div>
                                                                        <button
                                                                            onClick={() => handleAcknowledge(file)}
                                                                            className="w-full px-4 py-2 text-left text-sm text-emerald-600 hover:bg-emerald-50 flex items-center gap-2"
                                                                        >
                                                                            <CheckCircle size={16} />
                                                                            Mark as Reviewed
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {file.file_category === 'private' && (
                                                                    <>
                                                                        <div className="border-t border-border my-1"></div>
                                                                        <button
                                                                            onClick={() => handleDeleteFile(file)}
                                                                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                            Delete
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>


                                    {/* Pagination */}
                                    <div className="px-6 py-4 bg-muted/50 border-t border-border rounded-b-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <p className="text-sm text-muted-foreground">
                                            Showing {files.length} of {pagination.total} document{pagination.total !== 1 ? 's' : ''}
                                        </p>

                                        {pagination.pages > 1 && (
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handlePageChange(pagination.page - 1)}
                                                    disabled={pagination.page === 1}
                                                    className="p-2 border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <ChevronRight size={16} className="rotate-180" />
                                                </button>

                                                <div className="flex items-center gap-1">
                                                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                                        let p = i + 1;
                                                        if (pagination.pages > 5) {
                                                            if (pagination.page > 3) {
                                                                p = pagination.page - 2 + i;
                                                            }
                                                            if (p > pagination.pages) {
                                                                p = pagination.pages - 4 + i;
                                                            }
                                                        }

                                                        return (
                                                            <button
                                                                key={p}
                                                                onClick={() => handlePageChange(p)}
                                                                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${pagination.page === p
                                                                    ? 'bg-primary text-white'
                                                                    : 'text-muted-foreground hover:bg-muted'
                                                                    }`}
                                                            >
                                                                {p}
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                <button
                                                    onClick={() => handlePageChange(pagination.page + 1)}
                                                    disabled={pagination.page === pagination.pages}
                                                    className="p-2 border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <ChevronRight size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* AI Panel */}
                <EmployeeDocumentsAIPanel
                    isOpen={showAIPanel}
                    onToggle={() => setShowAIPanel(!showAIPanel)}
                    phase={lifecycleFilter !== 'all' ? lifecycleFilter : null}
                />
            </div>

            {/* Floating Action Button for Mobile/Tablet */}
            <button
                onClick={() => setShowAIPanel(true)}
                className="xl:hidden fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg shadow-primary/30 flex items-center justify-center z-30 transition-transform hover:scale-105 active:scale-95"
            >
                <Sparkles size={24} />
            </button>

            {/* Preview Modal */}
            {showPreviewModal && selectedFile && (
                <EmployeeFilePreviewModal
                    file={selectedFile}
                    onClose={() => {
                        setShowPreviewModal(false);
                        setSelectedFile(null);
                    }}
                />
            )}

            {/* Acknowledgment Modal */}
            {fileToAcknowledge && (
                <FileAcknowledgmentModal
                    file={fileToAcknowledge}
                    onClose={() => setFileToAcknowledge(null)}
                    onSuccess={handleAcknowledgeSuccess}
                />
            )}
        </div>
    );
};

export default EmployeeFilesPage;
